import { Injectable, inject, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { HelperDebugLoggerControllerService } from './helper-debug-logger-controller.service';

export interface CrashReport {
  id: string;
  timestamp: string;
  name: string;
  message: string;
  stack?: string;
  platform: string;
  appPersona: string;
  instanceTier: string;
  url: string;
  metadata?: Record<string, any>;
}

@Injectable({
  providedIn: 'root',
})
export class HelperCrashReportingControllerService {
  private debugLogger = inject(HelperDebugLoggerControllerService);

  private readonly STORAGE_KEY = 'bm_crash_reports';
  private readonly MAX_CRASH_HISTORY = 20;

  public readonly isInitialized = signal<boolean>(false);

  constructor() {
    this.init();
  }

  /**
   * Initializes global window error listeners and loads stored crash metrics
   */
  public async init(): Promise<void> {
    if (this.isInitialized()) {
      return;
    }

    if (environment.allowCrashReporting === false) {
      return;
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event: ErrorEvent) => this.handleGlobalError(event));
      window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => this.handleUnhandledRejection(event));
    }

    this.isInitialized.set(true);
  }

  public handleGlobalError(event: ErrorEvent | { error?: any; message?: string; filename?: string; lineno?: number; colno?: number }): void {
    this.captureException(event.error || new Error(event.message || 'Window Error'), {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  }

  public handleUnhandledRejection(event: PromiseRejectionEvent | { reason?: any }): void {
    this.captureException(event.reason || new Error('Unhandled Promise Rejection'), {
      source: 'unhandledrejection',
    });
  }

  /**
   * Captures and reports an exception with full device diagnostic context
   */
  public captureException(error: unknown, context: Record<string, any> = {}): CrashReport {
    const err = error instanceof Error ? error : new Error(String(error));

    const report: CrashReport = {
      id: `crash_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      timestamp: new Date().toISOString(),
      name: err.name || 'Error',
      message: err.message || 'Unknown error occurred',
      stack: err.stack,
      platform: Capacitor.getPlatform(),
      appPersona: environment.app,
      instanceTier: environment.instance,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      metadata: context,
    };

    // Store in local storage for persistence across reloads/crashes
    this.persistCrash(report);

    // Forward to in-app debug logger
    this.debugLogger.addLog('error', `[CRASH] ${report.name}: ${report.message}`, {
      stack: report.stack,
      metadata: context,
    });

    // Dispatch to Sentry endpoint if configured
    this.dispatchToSentry(report);

    return report;
  }

  /**
   * Retrieves all saved crash reports
   */
  public getStoredCrashes(): CrashReport[] {
    if (typeof window === 'undefined' || !window.localStorage) {
      return [];
    }

    try {
      const data = window.localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  private persistCrash(report: CrashReport): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    try {
      const reports = this.getStoredCrashes();
      reports.unshift(report);
      if (reports.length > this.MAX_CRASH_HISTORY) {
        reports.pop();
      }
      window.localStorage.setItem(this.STORAGE_KEY, JSON.stringify(reports));
    } catch {
      // Ignore storage quota errors
    }
  }

  private dispatchToSentry(report: CrashReport): void {
    const sentryDsn = environment.sentryDsn;
    if (!sentryDsn || typeof window === 'undefined' || !window.fetch) {
      return;
    }

    try {
      // Sends payload to configured Sentry ingest URL
      window.fetch(sentryDsn, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exception: {
            values: [{ type: report.name, value: report.message, stacktrace: { frames: report.stack } }],
          },
          tags: {
            platform: report.platform,
            persona: report.appPersona,
            tier: report.instanceTier,
          },
          extra: report.metadata,
        }),
      }).catch(() => {
        // Fail silently if offline / network error
      });
    } catch {
      // Fail silently
    }
  }
}
